import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DaoDocumentsService } from "src/app/providers/db/dao-document.service";
import { GlobalService, AnalyticsService } from "src/app/shared/services";
import { MenuController, ModalController, Platform } from "@ionic/angular";
import { Events } from 'src/app/shared/services/global/events.service';
import { NetworkService } from "src/app/providers/network/network.service";
import { StorageOfflineService } from "src/app/providers/storage-offline/storage-offline.service";
import { Subscription } from "rxjs";
import { EventColors } from "src/app/models/event-colors";
import { EventDataService } from "src/app/shared/services/eventData/event-data.service";
import { DocumentSearchService } from "src/app/shared/services/document-search/document-search.service";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/shared/reducers";
import { SetDocumentViewMode } from "src/app/shared/actions/interactivity.actions";
import { ViewMode, IBreadcrumbItem, IDocument } from "src/app/models/document.models";
import { DocumentViewerComponent } from "src/app/shared/components/documents/document-viewer/document-viewer.component";
import { DocumentService } from "src/app/providers/document/document.service";

@Component({
    selector: "app-documents-folder",
    templateUrl: "./documents-folder.page.html",
    styleUrls: ["./documents-folder.page.scss"],
})
export class DocumentsFolderPage implements OnInit, OnDestroy {
    subscriptions: Subscription[] = [];
    docModule: any = null;

    eventId: string = null;
    moduleId: string = null;
    folderId: string = null;
    isMobile: boolean = false;

    folder: any;

    documentsSaved: any[] = [];
    documents: any[] = [];

    loader: boolean = true;

    searchText: string = "";
    backBtn: boolean = true;

    eventColors: EventColors = new EventColors();

    menuBadge: number = 0;
    language: string;
    enableNoResult = false;

    viewMode: ViewMode = 'list';
    breadcrumbItems: IBreadcrumbItem[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private SAnalytics: AnalyticsService,
        private daoDocuments: DaoDocumentsService,
        public SGlobal: GlobalService,
        private events: Events,
        private menuCtrl: MenuController,
        private network: NetworkService,
        private storage: StorageOfflineService,
        private SEventData: EventDataService,
        private platform: Platform,
        private documentSearchService: DocumentSearchService,
        private store: Store<AppState>,
        private modalCtrl: ModalController,
        private documentService: DocumentService,
    ) {
        this.menuCtrl.enable(true);
        this.language = this.SEventData.getLanguage();
    }

    loadColors(eventId: string = this.eventId) {
        const subscription = this.SEventData.getEventDataListener(eventId).subscribe((eventData) => {
            this.eventColors = eventData.colors;
        });
        this.subscriptions.push(subscription);
    }

    ngOnInit() {
        if (this.platform.is('ios') || this.platform.is('android')) {
            this.isMobile = true;
        }

        this.backBtn = this.SGlobal.previousPage !== "container";

        this.route.params.subscribe((params) => {
            this.eventId = params.eventId;
            this.moduleId = params.moduleId;
            this.folderId = params.folderId;

            this.SAnalytics.goToDocumentsFolder(this.eventId, this.moduleId, this.folderId);

            this.loadColors();
            this.loadViewModePreference();
            this.menuBadge = this.SGlobal.notificationBadge;
            this.events.subscribe("menuBadge", () => {
                this.menuBadge = this.SGlobal.notificationBadge;
            });

            if (this.network.status()) {
                this.subscriptions.push(this.daoDocuments.getModule(this.moduleId).subscribe((module) => {
                    this.docModule = module;
                    this.updateBreadcrumb();

                    this.subscriptions.push(this.daoDocuments.getFolder(this.moduleId, this.folderId).subscribe((folder) => {
                        this.folder = folder;
                        this.updateBreadcrumb();

                        const orderDocuments = folder.orderDocuments;
                        const orderName = `name.${this.language}`;

                        this.subscriptions.push(this.daoDocuments.getDocuments(this.moduleId, this.folderId, orderDocuments, orderName).subscribe((documents) => {
                            this.documentsSaved = [...documents];
                            this.documents = [...documents].map(doc => {
                                if (doc.icon === 'jpg_ico.png' || doc.icon === 'png_ico.png') {
                                    doc.icon = 'photo_ico.svg';
                                }
                                if (doc.icon && typeof doc.icon === 'string' && doc.icon.endsWith('png')) {
                                    doc.icon = doc.icon.slice(0, -3) + 'svg';
                                }
                                return doc;
                            });
                            this.loader = false;
                            this.storage.setObject(this.folderId, {
                                module: this.docModule,
                                documents: this.documentsSaved,
                            });
                        }));
                    }));
                }));
            } else {
                this.storage
                    .getObject(this.folderId)
                    .then((result) => {
                        if (result != null) {
                            this.docModule = result.module;
                            this.documentsSaved = result.documents;
                            this.documents = result.documents;
                            this.updateBreadcrumb();
                        }
                        this.loader = false;
                    })
                    .catch(() => {
                        this.docModule = null;
                        this.documentsSaved = [];
                        this.documents = [];
                        this.loader = false;
                    });
            }
        });
    }

    private async loadViewModePreference() {
        try {
            const saved = await this.storage.getObject(`viewMode:documents`);
            if (saved) {
                this.viewMode = saved;
                this.store.dispatch(new SetDocumentViewMode({ viewMode: saved }));
            }
        } catch { }
    }

    private async saveViewModePreference(mode: ViewMode) {
        await this.storage.setObject(`viewMode:documents`, mode);
    }

    private updateBreadcrumb() {
        this.breadcrumbItems = [
            {
                label: 'Documents',
                path: `/event/${this.eventId}/documents/${this.moduleId}`,
            },
            {
                label: this.folder?.name?.[this.language] || '',
                path: '',
            },
        ];
    }

    onBreadcrumbNavigate(item: IBreadcrumbItem) {
        if (item.path) {
            this.router.navigateByUrl(item.path);
        }
    }

    onSearchChange(query: string) {
        this.documents = this.documentSearchService.filterDocuments(
            this.documentsSaved, query, this.language
        );
        this.enableNoResult = this.documents.length === 0 && query.trim().length > 0;
    }

    onSearchClear() {
        this.documents = [...this.documentsSaved];
        this.enableNoResult = false;
    }

    onViewModeChange(mode: ViewMode) {
        this.viewMode = mode;
        this.store.dispatch(new SetDocumentViewMode({ viewMode: mode }));
        this.saveViewModePreference(mode);
    }

  async onDocumentPreview(doc: IDocument) {
    await this.presentViewer(doc);
  }

  async onDownloadSelected(docs: IDocument[]) {
    for (const doc of docs) {
      await this.documentService.downloadDocument(doc, this.eventId);
    }
  }

  async presentViewer(doc: IDocument) {
        const modal = await this.modalCtrl.create({
            component: DocumentViewerComponent,
            componentProps: {
                document: doc,
                eventId: this.eventId,
            },
            cssClass: 'document-viewer-modal',
        });
        await modal.present();
    }

    trackByUid(_index: number, doc: IDocument): string {
        return doc.uid;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
